import { WORKSPACES_TOUR_ID } from '@/tour-guide/tours';

export { WORKSPACES_TOUR_ID };

export const WORKSPACES_RELEASED_AT = '';

export function effectiveReleasedAt(): string {
  return WORKSPACES_RELEASED_AT;
}

export function isTourForced(): boolean {
  try {
    const env = import.meta.env as Record<string, unknown> | undefined;
    if (!env?.DEV) return false;
    const raw = env.VITE_WORKSPACES_TOUR_FORCE;
    return raw === '1' || raw === 'true' || raw === true;
  } catch {
    return false;
  }
}

export function workspacesReleasedAtMs(
  releasedAt: string = WORKSPACES_RELEASED_AT,
): number | null {
  const raw = typeof releasedAt === 'string' ? releasedAt.trim() : '';
  if (!raw) return null;
  const ms = new Date(raw).getTime();
  return Number.isFinite(ms) ? ms : null;
}

export type TourEligibleUser = {
  created_at?: string | null;
  createdAt?: string | null;
} | null | undefined;

export function isPreWorkspacesUser(
  user: TourEligibleUser,
  releasedAt: string = WORKSPACES_RELEASED_AT,
): boolean {
  const cutoff = workspacesReleasedAtMs(releasedAt);
  if (cutoff === null) return false;

  const raw = user?.created_at ?? user?.createdAt ?? null;
  if (!raw) return false;

  const created = new Date(raw).getTime();
  if (!Number.isFinite(created)) return false;

  return created < cutoff;
}

export function seenToursFromPreferences(preferences: unknown): string[] {
  let prefs = preferences;
  if (typeof prefs === 'string') {
    try {
      prefs = JSON.parse(prefs);
    } catch {
      return [];
    }
  }
  if (!prefs || typeof prefs !== 'object' || Array.isArray(prefs)) return [];

  const seen = (prefs as Record<string, unknown>).seen_tours;
  if (!Array.isArray(seen)) return [];
  return seen.filter((v): v is string => typeof v === 'string' && v.length > 0);
}

export function hasSeenTour(preferences: unknown, tourId: string): boolean {
  return seenToursFromPreferences(preferences).includes(tourId);
}

export function countConnectedWorkspaces(
  workspaces: ReadonlyArray<{ business_account_id?: string | null }> | null | undefined,
): number {
  if (!Array.isArray(workspaces)) return 0;
  return workspaces.filter((w) => {
    const id = w?.business_account_id;
    return typeof id === 'string' && id.trim().length > 0;
  }).length;
}

export function withSeenTour(preferences: unknown, tourId: string): Record<string, unknown> {
  let prefs = preferences;
  if (typeof prefs === 'string') {
    try {
      prefs = JSON.parse(prefs);
    } catch {
      prefs = {};
    }
  }
  const base =
    prefs && typeof prefs === 'object' && !Array.isArray(prefs)
      ? (prefs as Record<string, unknown>)
      : {};

  const seen = seenToursFromPreferences(base);
  if (seen.includes(tourId)) return base;
  return { ...base, seen_tours: [...seen, tourId] };
}


export const WORKSPACE_TOUR_SEEN_KEY = 'wenext_workspace_tour_seen';

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

function defaultStorage(): StorageLike {
  if (typeof localStorage === 'undefined') {
    return { getItem: () => null, setItem: () => undefined };
  }
  return localStorage;
}

export function workspaceTourSeenStorageKey(userId?: string | null): string {
  const id = userId != null && String(userId).trim() ? String(userId).trim() : 'anonymous';
  return `${WORKSPACE_TOUR_SEEN_KEY}:${id}`;
}

export function readLocalSeenTours(
  userId?: string | null,
  storage: StorageLike = defaultStorage(),
): string[] {
  try {
    const raw = storage.getItem(workspaceTourSeenStorageKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is string => typeof v === 'string' && v.length > 0);
  } catch {
    return [];
  }
}

export function hasSeenTourLocally(
  userId: string | null | undefined,
  tourId: string,
  storage: StorageLike = defaultStorage(),
): boolean {
  return readLocalSeenTours(userId, storage).includes(tourId);
}

export function markTourSeenLocally(
  userId: string | null | undefined,
  tourId: string,
  storage: StorageLike = defaultStorage(),
): string[] {
  const seen = readLocalSeenTours(userId, storage);
  if (seen.includes(tourId)) return seen;
  const next = [...seen, tourId];
  try {
    storage.setItem(workspaceTourSeenStorageKey(userId), JSON.stringify(next));
  } catch {
  }
  return next;
}

export function syncSeenToursFromPreferences(
  userId: string | null | undefined,
  preferences: unknown,
  storage: StorageLike = defaultStorage(),
): string[] {
  const fromServer = seenToursFromPreferences(preferences);
  if (!fromServer.length) return readLocalSeenTours(userId, storage);

  const local = readLocalSeenTours(userId, storage);
  const merged = [...local];
  for (const id of fromServer) if (!merged.includes(id)) merged.push(id);
  if (merged.length !== local.length) {
    try {
      storage.setItem(workspaceTourSeenStorageKey(userId), JSON.stringify(merged));
    } catch {
    }
  }
  return merged;
}

export function shouldShowWorkspacesCard(args: {
  user: TourEligibleUser;
  preferences: unknown;
  isScreenBusy: boolean;
  releasedAt?: string;
  forced?: boolean;
  seenLocally?: boolean;
}): boolean {
  const { preferences, isScreenBusy, releasedAt, forced, seenLocally } = args;
  if (isScreenBusy) return false;
  if (forced) return true;
  if (seenLocally) return false;
  if (workspacesReleasedAtMs(releasedAt ?? WORKSPACES_RELEASED_AT) === null) return false;
  return !hasSeenTour(preferences, WORKSPACES_TOUR_ID);
}
